import type { EntityType } from "@/types/fieldRegistry";
import { getRelationTypeForField, normalizeFilterFieldPath, isCadenceFilterField } from "./relationalFieldRegistry";
import { supabase } from "@/integrations/supabase/client";
import {
  RELATION_MISSING,
  RELATIVE_DATE_PRESETS,
  isRelativeDatePreset,
  evaluateDateRule,
  evaluateGenericRule,
} from "./filters/operators";
import { invalidateSavedFilterCoverageCache } from "./filters/serverFilterCoverage";
import { splitFilterValue } from "./filters/valueTokens";

// Re-export para callers existentes que importam de filterEngine.
;

function isDateOperator(op: string): boolean {
  return RELATIVE_DATE_PRESETS.has(op) ||
    ["after", "before", "between"].includes(op);
}

function isDateField(campo: string): boolean {
  const dateFields = new Set([
    "created_at", "updated_at", "close_date", "won_at", "lost_at", "date",
    "last_contact_at", "completed_at",
  ]);
  const fieldName = campo.includes(".") ? campo.split(".")[1] : campo;
  return dateFields.has(fieldName);
}

/** Operadores em `tags` evaluam contra um array de tag-ids, não contra um único valor. */
function evaluateTagsOperator(record: any, operador: string, valor: string | null): boolean {
  const tags = Array.isArray(record.tags) ? record.tags : [];
  const tagIds = tags.map((t: any) => t?.id || t).filter(Boolean);
  const filterTagIds = splitFilterValue(valor);

  switch (operador) {
    case "contains":
      return filterTagIds.length > 0 && filterTagIds.some((id) => tagIds.includes(id));
    case "not_contains":
      return filterTagIds.length === 0 || !filterTagIds.some((id) => tagIds.includes(id));
    case "is_empty":
      return tagIds.length === 0;
    case "is_not_empty":
      return tagIds.length > 0;
    default:
      return true;
  }
}

function getFieldValue(record: any, campo: string, primaryEntity: EntityType): any {
  if (campo.includes(".")) {
    const segments = campo.split(".");
    const [entity, field, sub] = segments;

    // Custom fields da entidade primária no formato `custom_data.<field>`
    if (entity === "custom_data") {
      return record.custom_data?.[field];
    }

    // Custom fields de entidade relacionada: `<entity>.custom_data.<field>`
    if (segments.length === 3 && field === "custom_data") {
      if (entity === primaryEntity) return record.custom_data?.[sub];
      const related = record[entity];
      if (related === null || related === undefined) return RELATION_MISSING;
      if (Array.isArray(related)) return related;
      return related.custom_data?.[sub];
    }

    if (entity === primaryEntity) {
      // Schema: `deals.lead_source` — filtros salvos podem usar `deals.source` (UI).
      if (entity === "deals" && field === "source") {
        return record.lead_source ?? record.source;
      }
      return record[field];
    }
    const related = record[entity];
    if (related === null || related === undefined) return RELATION_MISSING;
    if (Array.isArray(related)) {
      return related;
    }
    return related[field];
  }
  return record[campo];
}

function evaluateRuleOnSingleRecord(
  record: any,
  campo: string,
  operador: string,
  valor: string | null,
  primaryEntity: EntityType
): boolean {
  const fieldName = campo.includes(".") ? campo.split(".")[1] : campo;

  if (fieldName === "tags" || campo.endsWith(".tags")) {
    const targetEntity = campo.includes(".") ? campo.split(".")[0] : primaryEntity;
    const target = targetEntity === primaryEntity ? record : record[targetEntity];
    if (!target) return operador === "is_empty";
    return evaluateTagsOperator(target, operador, valor);
  }

  const fieldValue = getFieldValue(record, campo, primaryEntity);

  if (
    isDateField(campo) ||
    isDateOperator(operador) ||
    ((operador === "equals" || operador === "not_equals") && isRelativeDatePreset(valor))
  ) {
    return evaluateDateRule(fieldValue, operador, valor);
  }

  return evaluateGenericRule(fieldValue, operador, valor);
}

function applyRule(
  record: any,
  rule: { campo_filtrado: string; operador: string; valor: string | null },
  primaryEntity: EntityType
): boolean {
  const { campo_filtrado, operador, valor } = rule;
  const campo = normalizeFilterFieldPath(String(campo_filtrado ?? "").trim());

  // Cadence virtual fields are pre-resolved server-side via the
  // filter_entity_ids_by_cadence RPC; they don't have a per-record value to
  // evaluate client-side, so treat them as always-passing here.
  if (isCadenceFilterField(campo)) return true;

  const relationType = getRelationTypeForField(primaryEntity, campo);

  if (relationType === "hasMany") {
    const [entity] = campo.split(".");
    const relatedArray = record[entity];

    if (!Array.isArray(relatedArray) || relatedArray.length === 0) {
      return operador === "is_empty";
    }

    return relatedArray.some((item: any) =>
      evaluateRuleOnSingleRecord(
        { ...item, [entity]: item },
        campo,
        operador,
        valor,
        entity as EntityType
      )
    );
  }

  return evaluateRuleOnSingleRecord(record, campo, operador, valor, primaryEntity);
}

export function applyFilterGroups(
  records: any[],
  filterData: { grupos: any[] },
  primaryEntity: EntityType
): any[] {
  if (!filterData?.grupos?.length) return records;

  return records.filter((record) => {
    return filterData.grupos.every((grupo: any) => {
      const regras = grupo.regras || grupo.filtro_regras || [];
      if (!regras.length) return true;

      const results = regras.map((regra: any) =>
        applyRule(record, regra, primaryEntity)
      );

      if (grupo.tipo_agrupamento === "AND") {
        return results.every(Boolean);
      }
      return results.some(Boolean);
    });
  });
}

/**
 * Backward-compatible wrapper matching the old applySavedDealFilterFromData signature.
 */
function applySavedFilterFromData(
  records: any[],
  filterData: any,
  primaryEntity: EntityType = "deals"
): any[] {
  return applyFilterGroups(records, filterData, primaryEntity);
}

/**
 * Check if a filter has a rule targeting a specific field.
 * Useful to know if the filter explicitly filters by status, so that
 * server-side exclusions (e.g. hiding won/lost) can be skipped.
 */
export function filterHasFieldRule(
  filterData: { grupos?: any[] } | null | undefined,
  fieldSuffix: string
): boolean {
  if (!filterData?.grupos) return false;
  return filterData.grupos.some((grupo: any) => {
    const regras = grupo.regras || grupo.filtro_regras || [];
    return regras.some((r: any) => {
      const campo: string = r.campo_filtrado || "";
      return campo === fieldSuffix || campo.endsWith(`.${fieldSuffix}`);
    });
  });
}

const ENTITY_VARIATIONS: Record<string, string[]> = {
  deal: ["deal", "deals"],
  deals: ["deal", "deals"],
  company: ["company", "companies"],
  companies: ["company", "companies"],
  activity: ["activity", "activities"],
  activities: ["activity", "activities"],
  lead: ["lead", "leads"],
  leads: ["lead", "leads"],
  work_item: ["work_item", "work_items"],
  work_items: ["work_item", "work_items"],
};

/**
 * In-memory cache for fetchFilterData — avoids redundant requests when multiple
 * hooks (useDealsByStage × N stages, useDealStageCounts) fetch the same filter
 * in a single page load. Reduces ~8 identical Supabase calls to 1.
 */
const filterDataCache = new Map<string, { data: any; timestamp: number }>();
const FILTER_CACHE_TTL = 30_000; // 30 seconds

/**
 * Limpa o cache em memória de `fetchFilterData` (ex.: após criar/editar/excluir filtro).
 * Sem `filterId`, remove todas as entradas.
 */
export function invalidateFetchFilterDataCache(filterId?: string | null): void {
  // A cobertura server-side é derivada das regras do filtro — muda junto.
  invalidateSavedFilterCoverageCache(filterId);
  if (filterId) {
    const prefix = `${filterId}:`;
    for (const key of [...filterDataCache.keys()]) {
      if (key.startsWith(prefix)) filterDataCache.delete(key);
    }
    return;
  }
  filterDataCache.clear();
}

/**
 * Para filtros salvos na lista de leads, regras em `deals.*` / `activities.*` são hasMany.
 * Sem embed na query, `record.deals` fica indefinido e o motor descarta todas as linhas.
 */
function getLeadFilterRelationEmbedNeeds(
  filterData: { grupos?: any[] } | null | undefined
): { deals: boolean; activities: boolean } {
  let deals = false;
  let activities = false;
  if (!filterData?.grupos?.length) return { deals: false, activities: false };
  for (const grupo of filterData.grupos) {
    const regras = grupo.regras || grupo.filtro_regras || [];
    for (const r of regras) {
      const c = normalizeFilterFieldPath(String(r.campo_filtrado || ""));
      if (c === "deals" || c.startsWith("deals.")) deals = true;
      if (c === "activities" || c.startsWith("activities.")) activities = true;
    }
  }
  return { deals, activities };
}

const FILTROS_FILTER_SELECT = `
      *,
      grupos:filtro_grupos_condicoes(
        *,
        regras:filtro_regras(*)
      )
    `;

function normalizeFiltroRow(data: any): { grupos: any[] } | null {
  if (!data) return null;
  const grupos = data.grupos ?? data.filtro_grupos_condicoes ?? [];
  if (!Array.isArray(grupos) || grupos.length === 0) return null;
  return { ...data, grupos };
}

/**
 * Carrega regras do filtro salvo. Opcionalmente restringe por `org_id` (recomendado em listas por org).
 * Se não achar com `entidade_principal` ∈ variações, tenta só por `id_filtro` (valores legados na UI/DB).
 */
export async function fetchFilterData(
  filterId: string,
  entityType: string,
  orgId?: string | null
): Promise<{ grupos: any[] } | null> {
  const cacheKey = `${filterId}:${entityType}:${orgId ?? ""}`;
  const cached = filterDataCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < FILTER_CACHE_TTL) {
    return cached.data;
  }

  const variations = ENTITY_VARIATIONS[entityType] || [entityType];

  const buildBase = () =>
    supabase.from("filtros").select(FILTROS_FILTER_SELECT).eq("id_filtro", filterId);

  let query = buildBase().in("entidade_principal", variations);
  if (orgId) query = query.eq("org_id", orgId);

  let { data, error } = await query.maybeSingle();

  let normalized = normalizeFiltroRow(data);
  if (error || !normalized) {
    let q2 = buildBase();
    if (orgId) q2 = q2.eq("org_id", orgId);
    const second = await q2.maybeSingle();
    normalized = normalizeFiltroRow(second.data);
    error = second.error;
  }

  if (error || !normalized) {
    filterDataCache.set(cacheKey, { data: null, timestamp: Date.now() });
    return null;
  }

  filterDataCache.set(cacheKey, { data: normalized, timestamp: Date.now() });
  return normalized;
}

/**
 * Convenience: fetch filter + apply to records in one call.
 */
export async function fetchAndApplyFilter(
  records: any[],
  filterId: string | undefined | null,
  primaryEntity: EntityType,
  orgId?: string | null
): Promise<any[]> {
  if (!filterId) return records;

  const filterData = await fetchFilterData(filterId, primaryEntity, orgId);
  if (!filterData?.grupos?.length) return records;

  return applyFilterGroups(records, filterData, primaryEntity);
}
